import React, { useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  addCategoryType,
  deleteCategoryType,
  fetchCategoryTypes,
  updateCategoryType,
} from "../../../actions/categoryTypeAction";
import { fetchAllCategories } from "../../../actions/categoryAction";
import { AlertConfirmModal } from "../../../components/Modals/AlertConfirmModal";
import { sortHelper } from "../../../utils/helpers/sortHelper";
import FormCategoryType from "./FormCategoryType";
import { hasAnyPermission } from "../../../utils/helpers/permissions";

const initialState = {
  id: "",
  type: "product",
  category_id: "",
  value: "",
  label: "",
  description: "",
};

const CategoryTypePage = (props) => {
  const {
    productDataTypes,
    materialDataTypes,
    categories,
    isLoading,
    error,
    fetchCategoryTypes,
    fetchAllCategories,
    addCategoryType,
    updateCategoryType,
    deleteCategoryType,
    userProfile,
  } = props;

  const [perPage, setPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchFromUrl = searchParams.get("search") || "";
  const [filterText, setFilterText] = useState(searchFromUrl);
  const [formState, setFormState] = useState(initialState);
  const canViewProducts = hasAnyPermission(userProfile, ["view-products"]);
  const canViewMaterials = hasAnyPermission(userProfile, ["view-materials"]);
  const [typeFilter, setTypeFilter] = useState("product");
  const { sortConfig, handleSort, getSortedData } = sortHelper.useSort();
  const visibleDataTypes = typeFilter === "material" ? materialDataTypes : productDataTypes;
  const visibleTypes = visibleDataTypes?.data || [];

  useEffect(() => {
    if (!canViewProducts && canViewMaterials) {
      setTypeFilter("material");
    }
  }, [canViewMaterials, canViewProducts]);

  const refreshTypes = () => fetchCategoryTypes(perPage, currentPage, searchFromUrl, { type: typeFilter });

  const openModal = () => {
    setFormState({ ...initialState, type: typeFilter });
    setIsEdit(false);
  };

  const closeModal = () => {
    setFormState(initialState);
    setIsEdit(false);
  };

  const handleAdd = async () => {
    const success = await addCategoryType(formState);

    if (success) {
      closeModal();
      refreshTypes();
    }

    return success;
  };

  const handleUpdate = async () => {
    const success = await updateCategoryType(formState.id, formState);

    if (success) {
      closeModal();
      refreshTypes();
    }

    return success;
  };

  const handleDelete = async (id) => {
    const success = await deleteCategoryType(id);

    if (success) {
      closeModal();
      refreshTypes();
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setFilterText(value);

    if (value) {
      setSearchParams({ search: value });
    } else {
      setSearchParams({});
    }
  };

  const debouncedFetch = useMemo(
    () =>
      debounce((page, limit, search) => {
        fetchCategoryTypes(limit, page, search, { type: typeFilter });
      }, 500),
    [fetchCategoryTypes, typeFilter],
  );

  useEffect(() => {
    fetchAllCategories(500);
  }, [fetchAllCategories]);

  useEffect(() => {
    debouncedFetch(currentPage, perPage, searchFromUrl);

    return () => {
      debouncedFetch.cancel();
    };
  }, [currentPage, perPage, searchFromUrl, debouncedFetch]);

  useEffect(() => {
    setFilterText(searchFromUrl);
    setCurrentPage(1);
  }, [searchFromUrl]);

  const handleTypeFilter = (nextType) => {
    setTypeFilter(nextType);
    setCurrentPage(1);
    setFormState({ ...initialState, type: nextType });
    setIsEdit(false);
  };

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h1 className="page-title fs-1 text-capitalize">Data Tipe / Jenis</h1>
              <div className="page-pretitle">master tipe produk dan material</div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              {(typeFilter === "product" ? canViewProducts : canViewMaterials) ? <button
                type="button"
                className="btn btn-primary btn-5 d-none d-sm-inline-block"
                data-bs-toggle="modal"
                data-bs-target="#modal-form-category-type"
                onClick={openModal}>
                Tambah tipe
              </button> : null}
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="card">
            <div className="card-table">
              <div className="card-header">
                <div className="row w-full">
                  <div className="col-lg-5 col-md-6 col-12">
                    <h3 className="card-title mb-0">Tipe / Jenis</h3>
                    <p className="text-secondary m-0">Master karakter kopi berdasarkan kategori</p>
                  </div>
                  <div className="col-lg-4 col-md-6 col-12 my-2 my-lg-0">
                    <div className="akas-scope-filter" role="group" aria-label="Filter tipe">
                      {canViewMaterials ? <button
                        type="button"
                        className={`btn ${typeFilter === "material" ? "btn-primary" : "btn-white"}`}
                        aria-pressed={typeFilter === "material"}
                        onClick={() => handleTypeFilter("material")}>
                        Material
                      </button> : null}
                      {canViewProducts ? <button
                        type="button"
                        className={`btn ${typeFilter === "product" ? "btn-primary" : "btn-white"}`}
                        aria-pressed={typeFilter === "product"}
                        onClick={() => handleTypeFilter("product")}>
                        Produk
                      </button> : null}
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6 col-12 my-md-0 my-2">
                    <div className="input-group input-group-flat w-auto">
                      <span className="input-group-text">
                        {isLoading ? (
                          <div
                            className="spinner-border spinner-border-sm text-primary text-center"
                            role="status"></div>
                        ) : (
                          <svg width="24" height="24" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                            <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
                            <path d="M21 21l-6 -6" />
                          </svg>
                        )}
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        autoComplete="off"
                        onChange={handleSearchChange}
                        placeholder="Cari..."
                        value={filterText}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {error ? <div className="alert alert-danger m-3 mb-0">{error}</div> : null}

              <div className="table-responsive">
                <table className="table table-vcenter table-selectable">
                  <thead className="table-header">
                    <tr>
                      <th className="sort-name py-3 w-1">No.</th>
                      <sortHelper.SortableHeader
                        label="Flag"
                        sortKey="type"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <sortHelper.SortableHeader
                        label="Kategori"
                        sortKey="category_label"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <sortHelper.SortableHeader
                        label="Kode"
                        sortKey="value"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <sortHelper.SortableHeader
                        label="Label"
                        sortKey="label"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <th className="sort-status fw-bold">Deskripsi</th>
                      <th className="text-start" style={{ width: "20%" }}></th>
                    </tr>
                  </thead>
                  <tbody className="table-tbody">
                    {isLoading ? (
                      [...Array(visibleTypes?.length || 5)].map((_, index) => (
                        <tr key={`loading-${index}`}>
                          <td className="sort-name py-4">{(currentPage - 1) * perPage + index + 1}.</td>
                          {[...Array(5)].map((__, placeholderIndex) => (
                            <td key={placeholderIndex}>
                              <div className="placeholder placeholder-lg w-75"></div>
                            </td>
                          ))}
                          <td>
                            <div className="placeholder placeholder-lg w-75"></div>
                          </td>
                        </tr>
                      ))
                    ) : visibleTypes.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center text-secondary py-5">
                          Belum ada data tipe.
                        </td>
                      </tr>
                    ) : (
                      getSortedData(visibleTypes).map((item, index) => (
                        <tr key={item.id}>
                          <td className="sort-name">{(currentPage - 1) * perPage + index + 1}.</td>
                          <td>
                            <span
                              className={`badge fw-bold ${item.type === "material" ? "bg-warning-lt" : "bg-primary-lt"}`}>
                              {item.type === "material" ? "Material" : "Produk"}
                            </span>
                          </td>
                          <td>{item.category_label}</td>
                          <td>
                            <span className="badge fw-bold">{item.value}</span>
                          </td>
                          <td>{item.label}</td>
                          <td className="text-secondary">{item.description || "-"}</td>
                          <td>
                            <div className="btn-list flex-nowrap justify-content-center">
                              <button
                                type="button"
                                className="btn btn-1 bg-secondary-lt"
                                data-bs-toggle="modal"
                                data-bs-target="#modal-form-category-type"
                                onClick={() => {
                                  setIsEdit(true);
                                  setFormState({
                                    id: item.id,
                                    type: item.type || "product",
                                    category_id: item.category_id || "",
                                    value: item.value || "",
                                    label: item.label || "",
                                    description: item.description || "",
                                  });
                                }}>
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn btn-1 bg-danger-lt"
                                data-bs-toggle="modal"
                                data-bs-target="#modal-alert-category-type"
                                onClick={() => setFormState({ ...initialState, id: item.id, label: item.label })}>
                                Hapus
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="card-footer d-flex align-items-center">
                <div className="dropdown">
                  <button className="btn dropdown-toggle" data-bs-toggle="dropdown">
                    <span id="page-count" className="me-1">
                      {perPage}
                    </span>
                    <span>records</span>
                  </button>
                  <div className="dropdown-menu">
                    {[10, 20, 50, 100].map((value) => (
                      <button key={value} className="dropdown-item" onClick={() => setPerPage(value)}>
                        {value} records
                      </button>
                    ))}
                  </div>
                </div>
                <p className="ms-2 my-auto text-secondary">
                  {visibleDataTypes?.per_page || 0} dari <span>{visibleDataTypes?.total || 0}</span> data
                </p>

                <ul className="pagination m-0 ms-auto">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((previous) => Math.max(previous - 1, 1))}>
                      prev
                    </button>
                  </li>
                  {visibleDataTypes?.links?.slice(1, -1)?.map((item, index) => {
                    const pageNumber = item.url ? new URL(item.url).searchParams.get("page") : null;

                    return (
                      <li key={index} className={`page-item ${item.active ? "active" : ""}`}>
                        <button
                          className="page-link"
                          disabled={!pageNumber}
                          onClick={() => pageNumber && setCurrentPage(Number(pageNumber))}>
                          {item.label}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${currentPage === visibleDataTypes?.last_page ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      disabled={!visibleDataTypes?.next_page_url}
                      onClick={() => setCurrentPage(Number(visibleDataTypes?.current_page) + 1)}>
                      next
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FormCategoryType
        isEdit={isEdit}
        closeModal={closeModal}
        formState={formState}
        setFormState={setFormState}
        categories={categories}
        isLoading={isLoading}
        handleAdd={handleAdd}
        handleUpdate={handleUpdate}
        allowedTypes={[
          ...(canViewProducts ? ["product"] : []),
          ...(canViewMaterials ? ["material"] : []),
        ]}
      />

      <AlertConfirmModal
        type="error"
        message={`Menghapus data ${formState.label}`}
        confirmText="Hapus"
        cancelText="Batal"
        idModal="alert-category-type"
        idData={formState.id}
        onConfirm={handleDelete}
        onCancel={closeModal}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  productDataTypes: state?.categoryTypes?.productDatas,
  materialDataTypes: state?.categoryTypes?.materialDatas,
  categories: state?.categories?.datas?.data || [],
  userProfile: state?.auth?.login?.profile,
  isLoading: state?.categoryTypes?.loading,
  error: state?.categoryTypes?.error,
});

export default connect(mapStateToProps, {
  fetchCategoryTypes,
  fetchAllCategories,
  addCategoryType,
  updateCategoryType,
  deleteCategoryType,
})(CategoryTypePage);
